import React, { forwardRef, InputHTMLAttributes } from 'react';
import { InputBase, InputBaseProps } from './InputBase';
import classNames from 'classnames';
import { Stack } from '../stack';
import { InputAddonOwnProps } from './InputAddon';
import { IconMinus, IconPlus } from '../icons/generated';
import { CSSValueWithLength } from '../../styles';
import { button_base_css, getButtonKind } from '../button/styles';
import { css } from '@emotion/react';
import {
  getInputSizeByInputStepperSize,
  getInputStepperButtonIconSize,
  getInputStepperButtonSizeStyle,
  getInputStepperHeightBySize,
} from './styles';
import { InputStatus, InputStepperSize } from './types';
import { semantic_colors } from '../../foundation';
import { useInputStepper } from './useInputStepper';

export interface InputStepperBaseType extends Omit<InputAddonOwnProps, 'position' | 'status' | 'size'> {
  className?: string;
  width?: CSSValueWithLength;
  /** @default 'medium' */
  size?: InputStepperSize;
  value?: number | null;
  defaultValue?: number;
  /** min, max validation보다 우선합니다 */
  status?: 'info' | 'error';
  /** @default 1 */
  step?: number;
  /** @default 0 */
  min?: number;
  /** @default Infinity */
  max?: number;
  onChange?: (value: number | null, validValue?: number) => void;
}

export type InputStepperProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'onChange' | 'width' | 'size' | 'placeholder'
> &
  InputStepperBaseType;

export const InputStepper = forwardRef<HTMLInputElement, InputStepperProps>(
  (
    {
      className,
      status,
      disabled,
      size = 'medium',
      onChange,
      step = 1,
      min = 0,
      max = Infinity,
      defaultValue,
      value,
      width,
      ...props
    },
    ref,
  ) => {
    const { current_status, minus_props, plus_props, handleInputChange } = useInputStepper<InputStatus>({
      value,
      onChange,
      min,
      max,
      step,
      disabled,
      status,
    });

    const getInputProps = (): InputBaseProps => ({
      status: current_status,
      type: 'text',
      value: value ?? '',
      defaultValue,
      size: getInputSizeByInputStepperSize(size),
      onChange: handleInputChange,
      disabled,
      ...props,
      style: {
        textAlign: 'center',
        paddingBlock: 0,
        backgroundColor: semantic_colors.background.surface,
        opacity: disabled ? 0.4 : 1,
        height: getInputStepperHeightBySize(size),
      },
      showStatusIcon: false,
    });

    const getButtonProps = (disabled: boolean) => ({
      css: [
        button_base_css,
        getButtonKind('outlined_secondary'),
        getInputStepperButtonSizeStyle(size),
        css`
          cursor: ${disabled ? 'not-allowed' : 'pointer'};
        `,
      ],
    });

    return (
      <Stack
        direction='row'
        width={width}
        align='center'
        className={classNames('pds-input-stepper', className)}
        gap={4}
      >
        <button type='button' {...getButtonProps(minus_props.disabled)} {...minus_props}>
          <IconMinus size={getInputStepperButtonIconSize(size)} />
        </button>
        <InputBase {...getInputProps()} ref={ref} />
        <button type='button' {...getButtonProps(plus_props.disabled)} {...plus_props}>
          <IconPlus size={getInputStepperButtonIconSize(size)} />
        </button>
      </Stack>
    );
  },
);
